import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { getMessageDetail } from "../gmail/api";
import { ExtractedLink, ParsedMessageMetadata } from "../gmail/types";
import { RiskResult, scoreEmail } from "../scoring/rules";
import LinkTable from "../ui/LinkTable";
import RiskBadge from "../ui/RiskBadge";
import Loading from "../ui/Loading";
import ErrorBanner from "../ui/ErrorBanner";
import Accordion from "../ui/Accordion";

export default function MessageDetailPage() {
  const { id } = useParams();
  const { token } = useAuth();
  const [metadata, setMetadata] = useState<ParsedMessageMetadata | null>(null);
  const [links, setLinks] = useState<ExtractedLink[]>([]);
  const [error, setError] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token || !id) return;
    setLoading(true);
    (async () => {
      try {
        const { metadata: meta, links: extracted } = await getMessageDetail(token, id);
        setMetadata(meta);
        setLinks(extracted);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Unable to fetch message");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, token]);

  const risk: RiskResult | null = useMemo(() => {
    if (!metadata) return null;
    return scoreEmail({
      from: metadata.from,
      replyTo: metadata.replyTo,
      subject: metadata.subject,
      snippet: metadata.snippet,
      headers: metadata.headers,
      links
    });
  }, [metadata, links]);

  const plainExplanation = useMemo(() => {
    if (!risk) return "";
    const parts: string[] = [];
    if (risk.level === "high") {
      parts.push("Multiple strong scam signals make this message high risk.");
    } else if (risk.level === "medium") {
      parts.push("We saw some warning signs so proceed carefully.");
    } else {
      parts.push("We didn't see urgent payment threats, suspicious sender mismatches, or sketchy links.");
    }
    const reasonMap: Record<string, string> = {
      "Reply-To domain differs from From domain": "The sender's reply address doesn't match who they say they are.",
      "Display name references amazon but domain does not": "The display name mentions a brand but the domain doesn't match.",
      "Display name references paypal but domain does not": "The display name mentions a brand but the domain doesn't match.",
      "Display name references apple but domain does not": "The display name mentions a brand but the domain doesn't match.",
      "Display name references microsoft but domain does not": "The display name mentions a brand but the domain doesn't match.",
      "Display name references bank but domain does not": "The display name mentions a brand but the domain doesn't match.",
      "Display name references chase but domain does not": "The display name mentions a brand but the domain doesn't match.",
      "Display name references wells fargo but domain does not": "The display name mentions a brand but the domain doesn't match.",
      "Display name references boa but domain does not": "The display name mentions a brand but the domain doesn't match.",
      "Suspicious link patterns detected": "We spotted links that look risky.",
      "Links pointing to IP addresses": "Links point directly to IP addresses instead of trusted domains.",
      "Links using insecure HTTP": "Some links use insecure HTTP instead of HTTPS.",
      "Urgent/pressure language detected": "The message uses urgent or pressure-filled language.",
      "Authentication checks failed (SPF/DKIM/DMARC)": "Email authentication checks are failing.",
      "Potentially dangerous attachment type": "There are attachments that could be unsafe.",
      "No strong scam indicators detected.": "We didn't spot major scam indicators."
    };

    for (const reason of risk.reasons) {
      if (reasonMap[reason]) {
        parts.push(reasonMap[reason]);
      }
    }

    if (parts.length === 0) {
      parts.push("We reviewed the usual warning signs and didn't see anything alarming.");
    }

    return parts.join(" ");
  }, [risk]);

  if (!token) {
    return (
      <div className="card">
        <p>Please sign in to view message details.</p>
      </div>
    );
  }

  if (loading) return <Loading label="Analyzing message" />;
  if (error) return <ErrorBanner message={error} />;
  if (!metadata || !risk) return null;

  return (
    <div className="card detail-card">
      <div className="score-hero">
        <div className={`score-badge ${risk.level}`}>
          <div className="score-label">Scam score</div>
          <div className="score-value">
            <RiskBadge level={risk.level} score={risk.score} />
            <span className="score-number">{risk.score} / 100</span>
          </div>
        </div>
        <p className="score-description">Higher scores mean more indicators of scam or phishing risk.</p>
      </div>

      <div className="detail-meta">
        <h2>{metadata.subject || "(No subject)"}</h2>
        <div className="list-meta">From: {metadata.from}</div>
        {metadata.replyTo && <div className="list-meta">Reply-To: {metadata.replyTo}</div>}
        {metadata.date && <div className="list-meta">Date: {new Date(metadata.date).toLocaleString()}</div>}
      </div>

      <Accordion title="Why this score?" defaultOpen={false}>
        <div className="why-score">
          <div>
            <h4>Plain English</h4>
            <p>{plainExplanation}</p>
          </div>
          <div>
            <h4>Technical reasons</h4>
            <ul>
              {risk.reasons.map((reason) => (
                <li key={reason}>{reason}</li>
              ))}
            </ul>
          </div>
        </div>
      </Accordion>

      {links.length > 0 ? (
        <Accordion title={`Links (${links.length})`} defaultOpen={false}>
          <LinkTable links={links} />
        </Accordion>
      ) : (
        <Accordion title="Links (0)" defaultOpen={false}>
          <p className="list-meta">No links were extracted from this message.</p>
        </Accordion>
      )}

      <h3>Authentication & headers</h3>
      <div className="notice">
        <div>From: {metadata.from ?? "Unknown"}</div>
        <div>Reply-To: {metadata.replyTo ?? "None"}</div>
        <div>Authentication-Results: {metadata.headers["authentication-results"] ?? "Not provided"}</div>
      </div>

      <Link to="/inbox" className="button-secondary" style={{ marginTop: "1rem", display: "inline-block" }}>
        Back to inbox
      </Link>
    </div>
  );
}
